import { IconButton, OdinIconSize, useTestTag } from '@myosh/odin-components';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RiskMatrixData, RiskMatrixDataItem, RiskMatrixDataProps } from '../../../@types/risk-matrix-field';
import { dataTag } from '../../../utils/data-tags';
import { findRiskMatrixCaption } from './risk-rating-functions';
import RiskRatingMatrixInfoDialog from './risk-rating-matrix-info-dialog';

function RiskRatingMatrix({
  isAdminMode = false,
  riskMatrixData,
  onRatingCellClick,
  handleConsequenceValueChange,
  handleLikelyhoodValueChange,
}: RiskMatrixDataProps) {
  const tagCreator = useTestTag('risk-rating');
  const { t } = useTranslation();

  const [isDataDescriptionDialogVisible, setIsDataDescriptionDialogVisible] = useState<{
    type?: 'consequence' | 'likelihood';
    visible: boolean;
  }>({ visible: false });

  const onHeaderCellClick = (riskMatrixCell: RiskMatrixDataItem) => {
    if (isAdminMode) {
      handleConsequenceValueChange?.(riskMatrixCell);
    }
  };

  const renderMatrixHeaderCells = (riskMatrixCellData: RiskMatrixData) => {
    if (riskMatrixCellData) {
      return (
        <thead ref={tagCreator('header-row')}>
          <tr>
            <th></th>
            <th colSpan={riskMatrixCellData.length - 1} className={infoTextCellStyle}>
              <div className="flex items-center gap-2">
                <span>{t('consequence')}</span>
                <IconButton
                  icon="Info"
                  iconSize={OdinIconSize.ExtraSmall}
                  onClick={() => setIsDataDescriptionDialogVisible({ type: 'consequence', visible: true })}
                />
              </div>
            </th>
          </tr>
          <tr>
            {riskMatrixCellData.map((riskMatrixCell: RiskMatrixDataItem, index: number) => {
              if (riskMatrixCell) {
                if (index === 0) {
                  return (
                    <th key={index} className={infoTextCellStyle}>
                      <div className="flex items-center gap-2">
                        <span>{t('likelihood')}</span>
                        <IconButton
                          icon="Info"
                          iconSize={OdinIconSize.ExtraSmall}
                          onClick={() => setIsDataDescriptionDialogVisible({ type: 'likelihood', visible: true })}
                        />
                      </div>
                    </th>
                  );
                }

                const consequenceCaptionValue = findRiskMatrixCaption(riskMatrixCell.caption);

                return (
                  <th
                    key={index}
                    className={headerCellsStyle}
                    ref={tagCreator(dataTag(consequenceCaptionValue))}
                    onClick={() => onHeaderCellClick(riskMatrixCell)}
                  >
                    {consequenceCaptionValue}
                  </th>
                );
              }
            })}
          </tr>
        </thead>
      );
    }
  };

  const onMatrixBodyCellClick = (riskMatrixCell: RiskMatrixDataItem, index: number) => {
    if (index > 0) {
      onRatingCellClick(riskMatrixCell);
    } else {
      if (isAdminMode) {
        handleLikelyhoodValueChange?.(riskMatrixCell);
      }
    }
  };

  const renderMatrixBodyCells = (riskMatrixCellData: RiskMatrixData, rowIndex: number) => {
    return (
      <tr key={rowIndex}>
        {riskMatrixCellData.map((riskMatrixCell: RiskMatrixDataItem, index: number) => {
          if (riskMatrixCell) {
            const isCellStyleConfigured = riskMatrixCell.backgroundColor && riskMatrixCell.textColor;
            const likelihoodCaptionValue = findRiskMatrixCaption(riskMatrixCell.caption);
            const riskLevelRatingValue = riskMatrixCell.rating;

            return (
              <td
                key={index}
                className={clsx({ [bodyCellsStyle]: index > 0, [headerCellsStyle]: index === 0 })}
                style={
                  isCellStyleConfigured
                    ? { background: riskMatrixCell.backgroundColor, color: riskMatrixCell.textColor }
                    : {}
                }
                onClick={() => onMatrixBodyCellClick(riskMatrixCell, index)}
              >
                {index === 0 ? (
                  <span ref={tagCreator(dataTag(riskLevelRatingValue || likelihoodCaptionValue))} className="text-left">
                    {riskLevelRatingValue || likelihoodCaptionValue}
                  </span>
                ) : (
                  <button
                    className="focus:rounded-sm focus:outline-none focus:ring-2 focus:ring-primary-1"
                    ref={tagCreator(dataTag(riskLevelRatingValue || likelihoodCaptionValue))}
                    type="button"
                  >
                    {riskLevelRatingValue || likelihoodCaptionValue}
                  </button>
                )}
              </td>
            );
          }
        })}
      </tr>
    );
  };

  function renderRiskMatrixCells(riskMatrixData: Array<RiskMatrixData>) {
    return riskMatrixData.map((riskMatrixDataCell: RiskMatrixData, index: number) => {
      if (index === 0) {
        return;
      }
      return renderMatrixBodyCells(riskMatrixDataCell, index);
    });
  }

  const onRiskMatrixInfoDialogClose = () => {
    setIsDataDescriptionDialogVisible({ visible: false, type: undefined });
  };

  const tableCellsStyle = 'p-1';
  const headerCellsStyle = clsx(`font-medium`, tableCellsStyle, { 'hover:cursor-pointer': isAdminMode });
  const bodyCellsStyle = clsx('text-center rounded cursor-pointer shadow hover:brightness-75', tableCellsStyle, {
    'hover:cursor-pointer': isAdminMode,
  });
  const infoTextCellStyle = clsx(`text-sm text-gray-2 font-normal text-left`, headerCellsStyle, {
    'hover:cursor-default': isAdminMode,
  });

  return (
    <>
      <table ref={tagCreator('matrix')} className="m-auto border-separate border-spacing-2">
        {riskMatrixData && renderMatrixHeaderCells(riskMatrixData[0])}
        <tbody>{riskMatrixData && renderRiskMatrixCells(riskMatrixData)}</tbody>
      </table>
      <RiskRatingMatrixInfoDialog
        isVisible={isDataDescriptionDialogVisible.visible}
        type={isDataDescriptionDialogVisible.type}
        data={riskMatrixData}
        onClose={onRiskMatrixInfoDialogClose}
      />
    </>
  );
}

export default RiskRatingMatrix;
